/**
 * MIT License
 *
 * Copyright (c) 2017 Artem Tyurin
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { LTWHP, Page } from 'react-pdf-highlighter';
import optimizeClientRects from 'react-pdf-highlighter/dist/esm/lib/optimize-client-rects';

type PdfSpanElement = { node: HTMLElement; pageNumber: number };
type LeftAndWidthBounds = {
  left: number;
  width: number;
};
interface Options {
  initialValues: LeftAndWidthBounds;
  scale: number;
}

const measureText = (el: HTMLElement, startOffset = 0, endOffset?: number) => {
  // The temporary canvas is used to measure text length in the DOM.
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { alpha: false });
  if (ctx) {
    ctx.font = `${el.style.fontSize} ${el.style.fontFamily}`;
    const { width } = ctx.measureText(
      el.textContent?.substring(startOffset, endOffset) || '',
    );
    return width;
  }
  return 0;
};

const getParentPdfJsElement = (node: HTMLElement | null) => {
  return node?.closest('div.textLayer span[role="presentation"]');
};

export const getAllSpansInRange = (
  range: Range,
  pages: Page[],
): PdfSpanElement[] => {
  const spans: PdfSpanElement[] = [];
  const firstNode = (
    range.startContainer.nodeName === 'SPAN'
      ? range.startContainer
      : getParentPdfJsElement(range.startContainer.parentElement)
  ) as HTMLElement;

  const lastNode = (
    range.endContainer.nodeName === 'SPAN'
      ? range.endContainer
      : getParentPdfJsElement(range.endContainer.parentElement)
  ) as HTMLElement;

  if (!firstNode || !lastNode) return spans;

  spans.push({ node: firstNode, pageNumber: pages[0].number });
  let currentNode = firstNode;
  let pageIndex = 0;
  let currentPage = pages[0];
  if (firstNode === lastNode) return spans;

  do {
    if (!currentNode || !currentNode.nextElementSibling) {
      // The PDF.js structure is span < div.TextLayer < div.page
      pageIndex++;

      if (pageIndex >= pages.length) break;

      currentPage = pages[pageIndex];
      const nextPageTextLayer = currentPage.node.querySelector('.textLayer');
      currentNode = nextPageTextLayer?.firstChild as HTMLElement;
    } else {
      currentNode = currentNode.nextElementSibling as HTMLElement;
    }
    if (currentNode && currentNode.nodeName === 'SPAN')
      spans.push({ node: currentNode, pageNumber: currentPage.number });
  } while (currentNode !== lastNode);

  return spans;
};

const fromPxToNumber = (px: string) => Number(px.replace('px', '') || 0);

/**
 * the width and left would be different in 3 cases:
 * 1) there is only one span, causing it to be first and last at the same time, we should adjust width and left
 * 2) the first span which the selection might not include all of it's contents, we adjust width and left of it.
 * 3) the last span which the selection might not include all of it's contents, we adjust width of it.
 * */
const calcWidthAndLeftBounds = (
  spans: PdfSpanElement[],
  span: HTMLElement,
  range: Range,
  { initialValues, scale }: Options,
): LeftAndWidthBounds => {
  const bounds = {
    left: initialValues.left,
    width: initialValues.width,
  };
  const spanIndex = spans.findIndex((s) => s.node === span);
  if (spanIndex === 0 && spanIndex === spans.length - 1) {
    const selectedTextWidth =
      measureText(span, range.startOffset, range.endOffset) * scale;
    const endTextWidth = measureText(span, range.endOffset) * scale;
    bounds.left = bounds.left + bounds.width - selectedTextWidth - endTextWidth;
    bounds.width = selectedTextWidth;
  } else if (spanIndex === 0) {
    const selectedTextWidth = measureText(span, range.startOffset) * scale;
    bounds.left = bounds.left + bounds.width - selectedTextWidth;
    bounds.width = selectedTextWidth;
  } else if (spanIndex === spans.length - 1) {
    bounds.width = measureText(span, 0, range.endOffset) * scale;
  }

  return bounds;
};

export const getClientRects = (range: Range, pages: Page[]): Array<LTWHP> => {
  const rects: LTWHP[] = [];
  const spans = getAllSpansInRange(range, pages);

  for (const { node: span, pageNumber } of spans) {
    if (span) {
      const styles = getComputedStyle(span);

      // PDF.js applies a scaleX that isn't automatically aplied to styles.width, we need to adjust that.
      let spanScaleX;
      try {
        spanScaleX = new WebKitCSSMatrix(styles.transform).a;
      } catch {
        /**
         * string `styles.transform` has the following format: matrix(a, b, c, d, tx, ty).
         * If the user browser doesn't support WebKitCSSMatrix, we extract the value from the string.
         */
        spanScaleX = Number(
          styles.transform.substring(
            styles.transform.indexOf('(') + 1,
            styles.transform.indexOf(','),
          ),
        );
      }
      const initialWidth =
        spanScaleX *
        (fromPxToNumber(styles.width) -
          fromPxToNumber(styles.paddingLeft) -
          fromPxToNumber(styles.paddingRight));

      const height =
        fromPxToNumber(styles.height) -
        fromPxToNumber(styles.paddingTop) -
        fromPxToNumber(styles.paddingBottom);
      const initialLeft = fromPxToNumber(styles.left);

      const { width, left } = calcWidthAndLeftBounds(spans, span, range, {
        initialValues: { width: initialWidth, left: initialLeft },
        scale: spanScaleX,
      });

      const highlightedRect = {
        top: fromPxToNumber(styles.top),
        left: left,
        width: width,
        height,
        pageNumber: pageNumber,
      } as LTWHP;
      rects.push(highlightedRect);
    }
  }

  return optimizeClientRects(rects);
};
